import type { IClickMapMetricData } from '@gem-sdk/heatmap-react';

import { IMetricFormatter } from '~/modules/analytics/types';
import { EClickRankType } from '@gem-sdk/heatmap-react';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

export type IClickRankMetricKey = keyof Pick<
  IClickMapMetricData,
  | 'totalClicked'
  | 'totalOrders'
  | 'totalRevenue'
  | 'totalSessions'
  | 'conversionRate'
  | 'revenuePerClick'
  | 'revenuePerSession'
>;

export interface IClickRankConfig {
  id: EClickRankType;
  labelKey: string;
  metricKey: IClickRankMetricKey;
  formatter: IMetricFormatter;
  template: string;
  getMetric: (elementInfo?: IClickMapMetricData<number | undefined, string>) => number;
}

export const useHeatmapClickRankConfig = () => {
  const { t } = useTranslation();

  const configs: IClickRankConfig[] = useMemo(
    () => [
      {
        id: EClickRankType.MostClicks,
        labelKey: t('Most clicks'),
        metricKey: 'totalClicked',
        formatter: IMetricFormatter.NUMBER_COMMAS,
        template: '[1]{{value}}[] clicks · [2]{{percentage}}[] of total',
        getMetric: (elementInfo) => Number(elementInfo?.totalClicked?.value) || 0,
      },
      {
        id: EClickRankType.Revenue,
        labelKey: t('Revenue'),
        metricKey: 'totalRevenue',
        formatter: IMetricFormatter.CURRENCY,
        template: '[1]{{value}}[]',
        getMetric: (elementInfo) => Number(elementInfo?.totalRevenue?.value) || 0,
      },
      {
        id: EClickRankType.ConversionRate,
        labelKey: t('Conversion rate'),
        metricKey: 'conversionRate',
        formatter: IMetricFormatter.PERCENTAGE_STRING,
        template: '[1]{{value}}[]',
        getMetric: (elementInfo) => Number(elementInfo?.conversionRate?.value) || 0,
      },
      {
        id: EClickRankType.RevenuePerClick,
        labelKey: t('Revenue per click'),
        metricKey: 'revenuePerClick',
        formatter: IMetricFormatter.CURRENCY,
        template: '[1]{{value}}[]',
        getMetric: (elementInfo) => Number(elementInfo?.revenuePerClick?.value) || 0,
      },
      {
        id: EClickRankType.RevenuePerSession,
        labelKey: t('Revenue per session'),
        metricKey: 'revenuePerSession',
        formatter: IMetricFormatter.CURRENCY,
        template: '[1]{{value}}[]',
        getMetric: (elementInfo) => Number(elementInfo?.revenuePerSession?.value) || 0,
      },
    ],
    [t],
  );

  const configMap = useMemo(
    () =>
      configs.reduce(
        (acc, config) => {
          acc[config.id] = config;
          return acc;
        },
        {} as Record<EClickRankType, IClickRankConfig>,
      ),
    [configs],
  );

  const selectItems = useMemo(
    () =>
      configs.map((config) => ({
        id: config.id,
        label: config.labelKey,
      })),
    [configs],
  );

  const getConfigById = useCallback(
    (id: EClickRankType): IClickRankConfig => {
      return configMap[id] ?? configMap[EClickRankType.MostClicks];
    },
    [configMap],
  );

  return {
    selectItems,
    getConfigById,
    configs,
    configMap,
  };
};
